import { ViewerControl, Mark, MouseTools, MouseTool, FitType } from '@prizmdoc/viewer-core';
import { easeUpToAValue } from 'utils/animate';
import { addAlpha } from 'utils/color';
import { HIGHLIGHTSDISAPPEAR, PRESENT_COLOR, SELECTION_COLOR, SHARED_COLOR } from './constants';
import { ZoomType, MouseToolType } from '../types';
import logger from 'utils/logger';

export const fixPosition = (
  { x, y }: { x: number; y: number },
  { width, height }: { width: number; height: number },
) => {
  //Make sure the tooltip doesn't go off the top or left side of the window or off the right or
  return {
    x: Math.max(0, Math.min(x, window.innerWidth - width)),
    y: Math.max(0, Math.min(y, window.innerHeight - height)),
  };
};

export const cropCanvas = (
  canvas: HTMLCanvasElement,
  width: number,
  height: number,
): HTMLCanvasElement => {
  const realWidth = Math.round(width * (canvas.width / parseInt(canvas.style.width.slice(0, -2))));
  const realHeight = Math.round(
    height * (canvas.height / parseInt(canvas.style.height.slice(0, -2))),
  );

  const newCanvas = document.createElement('canvas');
  newCanvas.width = realWidth;
  newCanvas.height = realHeight;
  newCanvas
    .getContext('2d')
    ?.drawImage(canvas, 0, 0, realWidth, realHeight, 0, 0, realWidth, realHeight);
  newCanvas.style.height = `${height}px`;
  newCanvas.style.width = `${width}px`;
  return newCanvas;
};

interface BoundingRectangle {
  x: number;
  y: number;
  width: number;
  height: number;
}

interface Coordinates {
  clientX: number;
  clientY: number;
}

interface ClientDimensions {
  clientWidth: number;
  clientHeight: number;
}

type ConvertedCoordinates = Coordinates & ClientDimensions;

interface ConversionParams {
  boundingRectangle: BoundingRectangle | undefined;
  pageNumber: number;
}

export const convert = (
  mark: any,
  viewerControl: typeof ViewerControl,
): ConvertedCoordinates | null => {
  try {
    // Validate inputs - check if mark and its properties exist
    if (!mark || !viewerControl || !mark.pageNumber) {
      console.warn('Invalid inputs to convert function:', { 
        hasMark: !!mark, 
        pageNumber: mark?.pageNumber, 
        hasViewerControl: !!viewerControl 
      });
      return null;
    }

    const { boundingRectangle, pageNumber } = mark;

    // Check if boundingRectangle exists and has required properties
    if (!boundingRectangle || 
        boundingRectangle.x === undefined || 
        boundingRectangle.y === undefined || 
        boundingRectangle.width === undefined || 
        boundingRectangle.height === undefined) {
      console.warn('Invalid bounding rectangle in convert function:', { 
        hasBoundingRectangle: !!boundingRectangle,
        x: boundingRectangle?.x,
        y: boundingRectangle?.y,
        width: boundingRectangle?.width,
        height: boundingRectangle?.height,
        pageNumber,
        markId: mark.uid 
      });
      return null;
    }

    const { x, y, width, height } = boundingRectangle;

    // convert start point with retry logic
    const tryConversion = (attemptNumber = 0): any => {
      try {
        const coords = viewerControl.convertPageToWindowCoordinates(pageNumber, {
          x,
          y,
        });
        
        if (coords && coords.clientX !== undefined && coords.clientY !== undefined) {
          return coords; // Success
        }
        
        // If we get here, conversion failed or returned invalid coordinates
        if (attemptNumber < 2) {
          return null; // Will trigger fallback or retry
        }
        
        return null;
      } catch (error) {
        console.warn(`Error during coordinate conversion attempt ${attemptNumber + 1}:`, error);
        return null;
      }
    };
    
    const startCoords = tryConversion();

    // Check if conversion was successful
    if (!startCoords || startCoords.clientX === undefined || startCoords.clientY === undefined) {
      console.warn('Failed to convert start coordinates:', { pageNumber, x, y, startCoords });
      
      // For markButtonTrigger marks, return a simple fallback position instead of null
      const isMarkButtonTrigger = mark.getData && mark.getData('subType') === 'markButtonTrigger';
      if (isMarkButtonTrigger) {
        // Return a simple, safe fallback position
        const viewportWidth = window.innerWidth;
        const viewportHeight = window.innerHeight;
        
        // Use a simple calculation for fallback positioning
        const fallbackX = Math.max(100, Math.min(viewportWidth * 0.2, 200));
        const fallbackY = Math.max(100, Math.min(viewportHeight * 0.2, 200));
        
        return {
          clientX: fallbackX,
          clientY: fallbackY,
          clientWidth: 200,
          clientHeight: 50
        };
      }
      
      return null;
    }

    const { clientX, clientY } = startCoords;

    // convert end point
    const endCoords = viewerControl.convertPageToWindowCoordinates(
      pageNumber,
      {
        x: x + width,
        y: y + height,
      },
    );

    // Check if conversion was successful
    if (!endCoords || endCoords.clientX === undefined || endCoords.clientY === undefined) {
      console.warn('Failed to convert end coordinates:', { pageNumber, endX: x + width, endY: y + height, endCoords });
      
      // For markButtonTrigger marks, calculate simple fallback end coordinates
      const isMarkButtonTrigger = mark.getData && mark.getData('subType') === 'markButtonTrigger';
      if (isMarkButtonTrigger) {
        return {
          clientX,
          clientY,
          clientWidth: 200,
          clientHeight: 50
        };
      }
      
      return null;
    }

    const { clientX: endX, clientY: endY } = endCoords;

    const result = { clientX, clientY, clientWidth: endX - clientX, clientHeight: endY - clientY };
    
    // Validate result
    if (isNaN(result.clientX) || isNaN(result.clientY) || isNaN(result.clientWidth) || isNaN(result.clientHeight)) {
      console.warn('Invalid conversion result:', result);
      return null;
    }

    return result;
  } catch (error) {
    console.error('Error in convert function:', error, { 
      pageNumber: mark?.pageNumber, 
      boundingRectangle: mark?.boundingRectangle ? { 
        x: mark.boundingRectangle.x, 
        y: mark.boundingRectangle.y, 
        width: mark.boundingRectangle.width, 
        height: mark.boundingRectangle.height 
      } : null,
      markId: mark?.uid
    });
    return null;
  }
};

type MarkDisappearCallback = (mark: typeof Mark) => void;
type ShouldExitFunction = () => boolean;

export const makeMarkDisappear = (
  mark: typeof Mark,
  callback?: MarkDisappearCallback,
  shouldExitFn?: ShouldExitFunction,
): void => {
  const fillColor = mark.fillColor;
  easeUpToAValue(
    mark.opacity || 255,
    0,
    (value: number) => {
      if (mark.type === Mark.Type.HighlightAnnotation) {
        mark.fillColor = addAlpha(fillColor, value);
      } else {
        mark.opacity = value;
      }
      if (callback && value === 0) callback(mark);
    },
    0,
    1000,
    shouldExitFn,
  ).catch(() => {});
};

const _setScrollTop = (element: Element, Y: number): Promise<void> => {
  const centerElement = element.clientHeight / 2;
  const startPoint = element.scrollTop + centerElement;
  const maxPoint = element.scrollHeight - centerElement;
  return easeUpToAValue(
    startPoint,
    Y * element.scrollHeight,
    (value: number) => element.scrollTo(element.scrollLeft, value - centerElement),
    maxPoint,
  );
};

const _setScrollLeft = (element: HTMLElement, X: number): Promise<void> => {
  const centerElement = element.clientWidth / 2;
  const startPoint = element.scrollLeft + centerElement;
  const maxPoint = element.scrollWidth - centerElement;
  return easeUpToAValue(
    startPoint,
    X * element.scrollWidth,
    (value: number) => element.scrollTo(value - centerElement, element.scrollTop),
    maxPoint,
  );
};

export const setScroll = (element: HTMLElement, X: number, Y: number): Promise<void> =>
  new Promise<void>((resolve) => {
    let count = 2;
    _setScrollTop(element, Y).then(() => {
      --count;
      if (!count) return setTimeout(resolve, 25);
    });
    _setScrollLeft(element, X).then(() => {
      --count;
      if (!count) return setTimeout(resolve, 25);
    });
  });

export const getZoomScaleInfo = (viewerControl: typeof ViewerControl) => {
  const scaleFactor = viewerControl?.getScaleFactor();
  return { scaleFactor };
};

export const getDocumentRotationInfo = (viewerControl: typeof ViewerControl) => {
  return { documentRotation: viewerControl.getPageRotation() };
};

export function deriveLocalPageFromGlobalPage(
  userInput: string, // e.g. "E5"
  doc: any,
  zeroBasedIndexing: boolean = false,
  btpActive?: boolean, // if btpActive is true then we will use the btp logic
): number {
  const docStart = parseInt(doc.startPage, 10); // e.g. 3
  const docPageCount = parseInt(doc.pageCount, 10);
  const rawDocStart = doc.startPage; // this is if the startPage contains a "."
  // if user input contains 2 '/' then the last element is the page number
  if (userInput.split('/').length === 3) {
    const parts = userInput.split('/');
    const lastPart = parts[parts.length - 1];
    if (lastPart && !isNaN(parseInt(lastPart, 10))) {
      return parseInt(lastPart, 10);
    }
  }
  if (
    !doc.globalPagePrefix?.toUpperCase() ||
    !userInput.startsWith(doc.globalPagePrefix?.toUpperCase())
  ) {
    return 1;
  }
  // if the user input contains a . then we use the rawDocStart and whatever comes after the FINAL . of the doc start we will substract from whatever comes after the FINAL . of the user input
  // e.g. userInput = "E5.5" and doc.startPage is 5.1, then we will subtract 5.1 from 5.5, strip the "5." and return 4
  if (rawDocStart.includes('.') && userInput.includes('.') && !btpActive) {
    const userInputParts = userInput.split('.');
    const docStartParts = rawDocStart.split('.');
    const userGlobalNum = parseFloat(userInputParts[userInputParts.length - 1]);
    const docStartNum = parseFloat(docStartParts[docStartParts.length - 1]);
    if (isNaN(userGlobalNum) || isNaN(docStartNum)) {
      // If parse fails, fallback to page 1
      return 1;
    }
    // 3) local page = userGlobalNum - docStartNum + offset
    let localPage = userGlobalNum - docStartNum + (zeroBasedIndexing ? 0 : 1);
    // 4) clamp localPage
    if (localPage < 1) localPage = 1;
    if (localPage > docPageCount) localPage = docPageCount;
    // 5) done
    return localPage;
  }

  // 1) Strip out the prefix to get the numeric portion
  //    e.g. userInput = "E5" => numericPart = "5"
  const numericPart = userInput.slice(doc.globalPagePrefix.length);

  // Convert that string to a number
  const userGlobalNum = parseInt(numericPart, 10);
  if (isNaN(userGlobalNum)) {
    return 1;
  }

  // local page = userGlobalNum - docStart + offset
  let localPage = userGlobalNum - docStart + (zeroBasedIndexing ? 0 : 1);
  // If the input contains a decimal, subtract the suffix of doc.startPage, not the integer global prefix
  if (userInput.includes('.')) {
    const parts = userInput.split('.');
    const lastPartNum = parseInt(parts[parts.length - 1], 10);
    if (!isNaN(lastPartNum)) {
      // Determine suffix of document startPage if it has a decimal
      const startParts = String(doc.startPage).split('.');
      const startSuffix =
        startParts.length > 1 ? parseInt(startParts[startParts.length - 1], 10) : 0;
      const offset = zeroBasedIndexing ? 0 : 1;
      localPage = lastPartNum - startSuffix + offset;
    }
  }

  if (localPage < 1) localPage = 1;
  if (localPage > docPageCount) localPage = docPageCount;
  return localPage;
}

export const getScrollInfo = (scrollContainer: HTMLElement) => {
  const X =
    (scrollContainer.scrollLeft + scrollContainer.clientWidth / 2) / scrollContainer.scrollWidth;
  const Y =
    (scrollContainer.scrollTop + scrollContainer.clientHeight / 2) / scrollContainer.scrollHeight;

  return { scroll: { X, Y } };
};

export const getScrollContainer = (viewerControl?: any) => {
  const pccPageListContainerWrappers = document.getElementsByClassName(
    'pccPageListContainerWrapper',
  );

  const pccPageListContainerWrapper =
    pccPageListContainerWrappers.length === 1
      ? (pccPageListContainerWrappers[0] as any)
      : (viewerControl?._cs?._tM?.firstElementChild?.firstElementChild as HTMLElement);

  return pccPageListContainerWrapper;
};

const isEmpty = (obj: any) => !obj || Object.keys(obj).length === 0;

export const createAnnotations = (
  viewerControl: typeof ViewerControl,
  highlights: Array<any>,
  userId: any,
  diff?: any,
) => {
  const safeFixHighlight = (highlight: any, marks: any) => {
    try {
      // Verify marks are still attached to the document before operating on them
      const allMarks = viewerControl.getAllMarks();
      
      if (highlight.burned) {
        const burnedCrossRef = marks.find(({ uid }: any) => uid === highlight.annotation.uid);
        if (burnedCrossRef && allMarks.some((m: any) => m.uid === burnedCrossRef.uid)) {
          burnedCrossRef.setText('');
        } else {
          console.warn('Cannot burn cross reference: mark not found in document', { markId: burnedCrossRef?.uid });
        }
      }
      
      if (highlight.createdBy && highlight.createdBy.id !== userId) {
        const sharedMark = marks.find(({ uid }: any) => uid === highlight.annotation.uid);
        if (sharedMark && allMarks.some((m: any) => m.uid === sharedMark.uid)) {
          //don't change color of shared cross refs per Frank's request 13.11.23
          if (sharedMark.type !== Mark.Type.TextAnnotation) {
            sharedMark.setFillColor(SHARED_COLOR);
          }
        } else {
          console.warn('Cannot set shared color: mark not found in document', { markId: sharedMark?.uid });
        }
      }
    } catch (error) {
      console.error('Error in safeFixHighlight:', error, { highlightId: highlight?.annotation?.uid });
    }
  };

  // Helper function to wait for marks to be properly attached
  const waitForMarksAttachment = (expectedMarkIds: string[], maxRetries = 10) => {
    return new Promise<any[]>((resolve) => {
      let retries = 0;
      
      const checkMarks = () => {
        const allMarks = viewerControl.getAllMarks();
        const attachedMarkIds = allMarks.map((m: any) => m.uid);
        const allAttached = expectedMarkIds.every(id => attachedMarkIds.includes(id));
        
        if (allAttached || retries >= maxRetries) {
          if (!allAttached) {
            console.warn('Some marks may not be properly attached after max retries', {
              expected: expectedMarkIds,
              attached: attachedMarkIds,
              missing: expectedMarkIds.filter(id => !attachedMarkIds.includes(id))
            });
          }
          resolve(allMarks);
        } else {
          retries++;
          setTimeout(checkMarks, 50); // Wait 50ms before next check
        }
      };
      
      // Start checking after a small delay to allow deserialization to complete
      setTimeout(checkMarks, 100);
    });
  };

  if (!diff) {
    return new Promise((resolve) => {
      try {
        const newMarks = viewerControl.deserializeMarks(highlights.map((x: any) => x.annotation));
        const expectedMarkIds = highlights.map((x: any) => x.annotation.uid);
        
        // Wait for marks to be properly attached before applying fixes
        waitForMarksAttachment(expectedMarkIds).then((attachedMarks) => {
          highlights.forEach((highlight: any) => {
            safeFixHighlight(highlight, attachedMarks);
          });
          resolve(attachedMarks);
        });
      } catch (error) {
        console.error('Error creating annotations without diff:', error);
        resolve([]);
      }
    });
  }

  if (isEmpty(diff)) {
    return new Promise((resolve) => resolve(viewerControl.getAllMarks()));
  }

  if (
    Object.values(diff).length === highlights.length &&
    Object.values(diff).filter(Boolean).length === 0
  ) {
    return new Promise((resolve) => {
      try {
        viewerControl.deleteAllMarks();
        resolve([]);
      } catch (error) {
        console.error('Error deleting all marks:', error);
        resolve(viewerControl.getAllMarks());
      }
    });
  }

  const marksToDeserialize: any[] = [];
  const marksToDelete: any[] = [];

  try {
    const newMarks = viewerControl.getAllMarks();
    Object.keys(diff).forEach((key: any) => {
      if (diff[key] === undefined) {
        const markToDelete = newMarks.find(({ uid }: any) => uid === highlights[key].annotation.uid);
        if (markToDelete) marksToDelete.push(markToDelete); // happened once that there was less marks than hightlights
      } else {
        const markToUpdate = newMarks.find(({ uid }: any) => uid === highlights[key].annotation.uid);
        if (markToUpdate) marksToDelete.push(markToUpdate);
        marksToDeserialize.push(highlights[key].annotation);
      }
    });

    if (marksToDelete.length > 0) {
      try {
        viewerControl.deleteMarks(marksToDelete);
      } catch (error) {
        console.error('Error deleting marks during createAnnotations:', error);
      }
    }

    if (marksToDeserialize.length > 0) {
      return new Promise((resolve) => {
        try {
          viewerControl.deserializeMarks(marksToDeserialize);
          const expectedMarkIds = marksToDeserialize.map((x: any) => x.uid);
          
          // Wait for new marks to be properly attached
          waitForMarksAttachment(expectedMarkIds).then((allMarks) => {
            // Apply fixes to the newly created marks
            highlights.forEach((highlight: any) => {
              if (expectedMarkIds.includes(highlight.annotation.uid)) {
                safeFixHighlight(highlight, allMarks);
              }
            });
            resolve(allMarks);
          });
        } catch (error) {
          console.error('Error deserializing marks:', error);
          resolve(viewerControl.getAllMarks());
        }
      });
    }

    return new Promise((resolve) =>
      resolve(marksToDelete.length === 0 ? newMarks : viewerControl.getAllMarks()),
    );
  } catch (error) {
    console.error('Error in createAnnotations with diff:', error);
    return new Promise((resolve) => resolve(viewerControl.getAllMarks()));
  }
};

export const setSearchFunction = (
  viewerControl: any,
  searchHandler: any,
  searchFunctionsHandler: any,
) => {
  let searchResults: any[] = [];
  let currentSearchResultIndex = -1;
  let searchRequest: any = null;

  const search = (searchTerm: string) => {
    if (searchRequest && !searchRequest.isComplete) {
      searchRequest.cancel();
    }

    if (searchHandler) searchHandler(getSearchPayload(true));
    if (searchFunctionsHandler) {
      searchFunctionsHandler(getSearchFunctions());
    }

    setTimeout(() => {
      searchRequest = viewerControl.search(searchTerm);

      const searchHandlerFn = ({ partialSearchResults }: any) => {
        searchResults = partialSearchResults || [];
        currentSearchResultIndex = -1;
        searchNext();
        searchRequest.off('PartialSearchResultsAvailable', searchHandlerFn);
      };

      const searchHandlerFullFn = ({ completedSearchResults }: any) => {
        searchResults = completedSearchResults || [];
        if (searchHandler) {
          searchHandler(getSearchPayload(false));
        }
        if (searchFunctionsHandler) {
          searchFunctionsHandler(getSearchFunctions());
        }
        searchRequest.off('SearchCompleted', searchHandlerFullFn);
      };

      searchRequest.on('PartialSearchResultsAvailable', searchHandlerFn);
      searchRequest.on('SearchCompleted', searchHandlerFullFn);
    });
  };

  const searchNext = () => {
    if (searchResults && searchResults.length > 0) {
      if (currentSearchResultIndex >= searchResults.length - 1) currentSearchResultIndex = 0;
      else ++currentSearchResultIndex;

      viewerControl.scrollTo(searchResults[currentSearchResultIndex]);
    }
  };

  const searchPrev = () => {
    if (searchResults && searchResults.length > 0) {
      if (currentSearchResultIndex <= 0) currentSearchResultIndex = 0;
      else --currentSearchResultIndex;

      viewerControl.scrollTo(searchResults[currentSearchResultIndex]);
    }
  };

  const getSearchPayload = (isLoading: boolean = false) => ({
    isReady: true,
    isLoading,
  });

  const getSearchFunctions = () => ({
    search,
    searchNext,
    searchPrev,
    searchChanged: () => {
      if (searchResults.length > 0 && true) {
        viewerControl.clearSearch();
      }
    },
    highlightLocation: (location: any) => {
      if (location) {
        // Clear existing navigation highlights before adding new ones to prevent stacking
        try {
          if (typeof viewerControl.getAllMarks === 'function') {
            const allMarks = viewerControl.getAllMarks();
            if (allMarks && Array.isArray(allMarks)) {
              const navigationHighlightsToRemove = allMarks.filter((mark: any) => {
                try {
                  if (!mark || typeof mark.getData !== 'function') return false;
                  
                  const subType = mark.getData('subType');
                  const isNavigationHighlight = subType === 'highlightEvent' || subType === 'markButtonTrigger';
                  
                  // Only remove navigation highlights, regardless of page
                  return isNavigationHighlight;
                } catch {
                  return false;
                }
              });

              if (navigationHighlightsToRemove.length > 0 && typeof viewerControl.deleteMarks === 'function') {
                // Force hide mark buttons immediately before deleting marks
                navigationHighlightsToRemove.forEach((mark: any) => {
                  try {
                    // Dispatch a custom event to force hide mark buttons
                    if (mark.getData && mark.getData('subType') === 'markButtonTrigger') {
                      const event = new CustomEvent('forceHideMarkButtons', { 
                        detail: { markId: mark.uid } 
                      });
                      window.dispatchEvent(event);
                    }
                  } catch (e) {
                    console.warn('Error dispatching hide event for mark:', mark.uid);
                  }
                });
                
                viewerControl.deleteMarks(navigationHighlightsToRemove);
              }
            }
          }
        } catch (error) {
          console.log('Error clearing existing navigation highlights:', error);
        }

        viewerControl
          .scrollToAsync({ pageNumber: location.pageNumber, x: location.x, y: location.y })
          .then(
            function () {
              viewerControl.requestPageText(location.pageNumber).then(
                function () {
                  // Check for existing overlapping persistent marks before highlighting
                  let hasOverlappingPersistentMark = false;
                    
                    try {
                    if (typeof viewerControl.getAllMarks === 'function') {
                      const allMarks = viewerControl.getAllMarks();
                      if (allMarks && Array.isArray(allMarks)) {
                        // Filter marks on the same page (excluding navigation highlights)
                        const persistentPageMarks = allMarks.filter((mark: any) => {
                          try {
                            if (!mark || typeof mark.getPageNumber !== 'function') return false;
                            if (mark.getPageNumber() !== location.pageNumber) return false;
                            
                            // Exclude navigation highlights - we only want to check persistent marks
                            const subType = typeof mark.getData === 'function' ? mark.getData('subType') : null;
                            const isNavigationHighlight = subType === 'highlightEvent' || subType === 'markButtonTrigger';
                            
                            return !isNavigationHighlight;
                          } catch {
                            return false;
                          }
                        });
                        
                        // Check for overlaps with persistent marks only (only prevent if >95% overlap)
                        hasOverlappingPersistentMark = persistentPageMarks.some((mark: any) => {
                          try {
                            if (typeof mark.getPosition === 'function') {
                              const markPosition = mark.getPosition();
                              if (markPosition && markPosition.startIndex !== undefined && markPosition.length !== undefined) {
                                const markStart = markPosition.startIndex;
                                const markEnd = markStart + markPosition.length;
                                const newStart = location.startIndex;
                                const newEnd = newStart + location.textLength;
                                
                                // Calculate overlap
                                const overlapStart = Math.max(markStart, newStart);
                                const overlapEnd = Math.min(markEnd, newEnd);
                                const overlapLength = Math.max(0, overlapEnd - overlapStart);
                                
                                if (overlapLength > 0) {
                                  // Calculate overlap percentage based on the new highlight length
                                  const overlapPercentage = overlapLength / location.textLength;
                                  
                                  // Only prevent highlighting if overlap is more than 95%
                                  return overlapPercentage > 0.95;
                                }
                              }
                            }
                            return false;
                          } catch {
                            return false;
                          }
                        });
                      }
                    }
                  } catch (error) {
                    console.warn('Error checking for overlapping persistent marks:', error);
                  }
                  
                  // Only create highlight if there's no overlapping persistent mark
                  if (!hasOverlappingPersistentMark) {
                    // Check if we should show mark button based on context
                    // Priority: explicit context from location object, then AI tools URL, then default
                    let shouldShowMarkButton = false;
                    
                    // Check if location object has explicit context information
                    if (location.showMarkButton !== undefined) {
                      shouldShowMarkButton = location.showMarkButton;
                    } else {
                      // Fallback to URL-based detection for AI tools
                      shouldShowMarkButton = window.location.href.includes('ai-tools');
                    }
                    
                    const subType = shouldShowMarkButton ? 'markButtonTrigger' : 'highlightEvent';
                    
                    const highlightedArea = viewerControl
                      .addMark(location.pageNumber, 'HighlightAnnotation')
                      .setPosition({
                        startIndex: location.startIndex,
                        length: location.textLength,
                      })
                      .setData('subType', subType);
                    highlightedArea.setFillColor('#AFD0F9');
                    
                    // Give the viewer a moment to register the mark in its internal registry
                    // This helps prevent the "Mark not found" error in MarkButton components
                    setTimeout(() => {
                      // Mark should now be fully registered and accessible to MarkButton components
                    }, 50);
                  }
                },
                function (error: any) {
                  logger.ERROR('requestPageText failed', error);
                },
              );
            },
            function (error: any) {
              logger.ERROR('scrollToAsync failed', error);
            },
          );
      }
    },
  });

  if (searchHandler) {
    searchHandler(getSearchPayload());
  }
  if (searchFunctionsHandler) {
    searchFunctionsHandler(getSearchFunctions());
  }
};

export const setMouse = (selMouseTool: string, isPresenting: boolean, viewerControl: any) => {
  switch (selMouseTool) {
    case MouseToolType.HighlightAnnotation: {
      const myMouseToolName = 'MyHighlightTool';
      const myMouseTool = MouseTools.createMouseTool(
        myMouseToolName,
        MouseTool.Type.HighlightAnnotation,
      );
      myMouseTool.getTemplateMark().setFillColor(isPresenting ? PRESENT_COLOR : SELECTION_COLOR);
      if (viewerControl) viewerControl.setCurrentMouseTool(myMouseToolName);
      return;
    }
    case MouseToolType.RectangleAnnotation: {
      const myMouseToolName = 'MyRectangleTool';
      const myMouseTool = MouseTools.createMouseTool(
        myMouseToolName,
        MouseTool.Type.RectangleAnnotation,
      );
      myMouseTool.getTemplateMark().opacity = HIGHLIGHTSDISAPPEAR && isPresenting ? 150 : 80;
      myMouseTool.getTemplateMark().borderThickness = 1;
      myMouseTool.getTemplateMark().borderColor = '#000000';
      myMouseTool.getTemplateMark().setFillColor(isPresenting ? PRESENT_COLOR : SELECTION_COLOR);
      if (viewerControl) viewerControl.setCurrentMouseTool(myMouseToolName);
      return;
    }
    default: {
      const myMouseToolName = 'MyPanTool';
      MouseTools.createMouseTool(myMouseToolName, MouseTool.Type.PanAndEdit);
      if (viewerControl) viewerControl.setCurrentMouseTool(myMouseToolName);
      return;
    }
  }
};

export const zoom = (type: string, viewerControl: any) => {
  switch (type) {
    case ZoomType.In:
      return viewerControl.zoomIn(1.25);
    case ZoomType.Out:
      return viewerControl.zoomOut(1.25);
    case ZoomType.Page:
      return viewerControl.fitContent(FitType.FullPage);
    case ZoomType.Width:
      return viewerControl.fitContent(FitType.FullWidth);
    case ZoomType.Summary:
      return viewerControl.zoomOut(1.2);
    default:
      return;
  }
};

export const containsText = (text: any, st: any) =>
  text.toLowerCase().indexOf(st.toLowerCase()) > -1;

export const setOverflowHidden = () => {
  const pccPageListContainerWrapper = getScrollContainer();
  if (pccPageListContainerWrapper) pccPageListContainerWrapper.style.overflow = 'hidden';
};
